import React, { useEffect, useRef } from "react"
import { useEditor, EditorContent } from "@tiptap/react"
import Document from "@tiptap/extension-document"
import Text from "@tiptap/extension-text"
import Paragraph from "@tiptap/extension-paragraph"
import History from "@tiptap/extension-history"
import Placeholder from "@tiptap/extension-placeholder"
import SmartClipboardExtension from "shared/TipTap/Extensions/SmartClipboardExtension"
import { QuerySyntaxHighlight } from "features/QueryConsole/components/Extensions/QuerySyntaxHighlightingExtension"
import { QueryIndent } from "features/QueryConsole/components/Extensions/QueryIndentExtension"
import DataQButton from "shared/ui/Buttons/DataQButton.component"

interface ScriptEditorProps {
  value: string
  onChange: (text: string) => void
  onRun: () => void
  /** The filter declaration sent in front of the script, shown read only. */
  prefixPreview: string
  /** Caption of the button that calls `onRun`; default "Run". */
  runLabel?: string
  /** Accessible name of the editor; default "Tile script". */
  ariaLabel?: string
  /** Shown while the editor is empty. */
  placeholder?: string
}

const TILE_PLACEHOLDER =
  "Write the tile's script. Use {{universe}} where the filter should apply."

/** One paragraph per line, as the console editor holds a script. */
const toDoc = (text: string) => ({
  type: "doc",
  content: text.split("\n").map((line) => ({
    type: "paragraph",
    content: line ? [{ type: "text", text: line }] : [],
  })),
})

/** The editor's text as a script: paragraphs joined by single newlines. */
const scriptText = (text: string): string => text.replace(/\n\n+/g, "\n")

/**
 * A script editor: the console's TipTap setup (syntax colours, auto-indent,
 * plain-text clipboard) without the slash-command popup, which needs the
 * console's engine globals. Script tiles use it, and so does the dashboard's
 * preparation script (with its own caption and name).
 */
const ScriptEditor: React.FC<ScriptEditorProps> = ({
  value,
  onChange,
  onRun,
  prefixPreview,
  runLabel = "Run",
  ariaLabel = "Tile script",
  placeholder = TILE_PLACEHOLDER,
}) => {
  // The editor keeps the callback it was created with; read the latest.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const editor = useEditor({
    extensions: [
      Document,
      Text,
      Paragraph,
      History,
      SmartClipboardExtension,
      QuerySyntaxHighlight,
      QueryIndent,
      Placeholder.configure({ placeholder }),
    ],
    content: toDoc(value),
    editorProps: {
      attributes: {
        class: "dq-script-editor",
        "aria-label": ariaLabel,
        spellcheck: "false",
      },
    },
    onUpdate: ({ editor: e }) => onChangeRef.current(scriptText(e.getText())),
  })

  // An outside change (another tile, a reset) replaces the content without
  // echoing it back through onChange.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    if (scriptText(editor.getText()) !== value) {
      editor.commands.setContent(toDoc(value), { emitUpdate: false })
    }
  }, [editor, value])

  return (
    <div className="dq-script">
      {prefixPreview && (
        <div className="dq-script__prefix">
          <span className="dq-script__caption">
            Sent before the script, for the filters
          </span>
          <pre aria-label="Filter prefix (read only)">{prefixPreview}</pre>
        </div>
      )}
      <EditorContent editor={editor} />
      <div className="dq-script__actions">
        <DataQButton type="primary" size="small" onClick={onRun}>
          {runLabel}
        </DataQButton>
      </div>
    </div>
  )
}

export default ScriptEditor
